import React, { useState, useContext } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { useGlobalEvent } from 'beautiful-react-hooks';
import { useMutation, useQueryClient } from 'react-query';

import FlashContext from '~/App/Components/Flash/Context';
import { togglePaymentSuccessModal } from '~/App/Components/Modals/UpgradeModals/PaymentSuccessModal';
import useStripe from '~/react-hooks/use-stripe';
import { purchaseAddOn } from '~/App/Modules/Billing/api';
import { AddOnType } from '~/types/models/addon';
import {
  CHECKOUT_COMPLETED,
  STRIPE_MODAL_PAYMENT_PAY,
  STRIPE_MODAL_PAYMENT_SUCCESS,
} from '~/generated/AnalyticsTypes';

import useAnalytics from '~/react-hooks/use-analytics';
import { StripePaymentModal } from '~/App/Components/Modals/UpgradeModalFlow/Modals/StripePaymentModal';
import { UpgradeModalViewContext } from '~/App/Services/BillingAnalytics';

const openEventName = 'custom-domain-add-on-payment-modal::open';

export const openCustomDomainAddOnPaymentModal = () => {
  window.dispatchEvent(new Event(openEventName));
};

export const successModalId = 'custom-domain-add-on-payment-success-modal';

type CustomDomainAddOnPaymentModalProps = {
  amount: number;
};

export default function CustomDomainAddOnPaymentModal({
  amount,
}: CustomDomainAddOnPaymentModalProps) {
  const { createFlash } = useContext(FlashContext);
  const stripe = useStripe();
  const [open, setOpen] = useState(false);
  const onRequestOpen = useGlobalEvent(openEventName as keyof WindowEventMap);
  onRequestOpen(() => setOpen(true));
  const queryClient = useQueryClient();
  const analytics = useAnalytics();

  const closePaymentModal = () => setOpen(false);

  const trackPurchaseEvent = (event: string) =>
    analytics.track(event, {
      context: 'custom_domain_addon',
      price: amount,
    });

  const { mutateAsync: purchaseAddOnMutation, isLoading } = useMutation(
    purchaseAddOn,
    {
      onSuccess: () => {
        trackPurchaseEvent(STRIPE_MODAL_PAYMENT_SUCCESS);

        analytics.track(CHECKOUT_COMPLETED, {
          context: UpgradeModalViewContext.CustomDomainAddon,
        });

        togglePaymentSuccessModal(successModalId, 'open');
        queryClient.invalidateQueries('app-config');
      },
      onError: () => {
        createFlash({
          id: 'purchase-add-on-failed',
          title: 'Error',
          message:
            'Something went wrong while processing your request. Please try again.',
          options: { style: 'danger' },
        });
      },
      onSettled: () => closePaymentModal(),
    }
  );

  const handleFormSubmit = () => {
    trackPurchaseEvent(STRIPE_MODAL_PAYMENT_PAY);
    purchaseAddOnMutation({ addon: AddOnType.CustomDomain });
  };

  const handleRequestClose = () => {
    if (isLoading) return;
    closePaymentModal();
  };

  return (
    <Elements stripe={stripe}>
      <StripePaymentModal
        amount={amount}
        open={open}
        onCloseClick={handleRequestClose}
        onSubmitClick={handleFormSubmit}
        loading={isLoading}
      />
    </Elements>
  );
}
